'use client';

import { HeaderAuthSection } from '@/components/auth';
import { SidebarMenuContent } from '@/components/layout/SidebarMenuContent';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { usePathname } from '@/i18n/routing';
import { Menu } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

interface HamburgerMenuProps {
  /**
   * Theme/locale switcher row inside the sheet. On when the host header hides
   * its own switchers at some widths; off when they're always visible beside
   * the trigger.
   */
  showSwitchers?: boolean;
  /**
   * Include the advocate landing page link (#7951 launch checklist item 3).
   * Resolved from the build-time `ADVOCATE_LANDING_ENABLED` env by the server
   * component that renders the header, and passed down — the value has no
   * `NEXT_PUBLIC_` prefix, so client modules cannot read it themselves.
   */
  includeAdvocateLanding?: boolean;
  /**
   * Render sign-in / sign-up (or the account button) at the top of the sheet,
   * shown only below `md` — the widths at which the header row hides at least
   * one of the two auth actions (the nav is `hidden sm:flex`, and sign-up is
   * additionally `hidden md:flex` inside it).
   *
   * Without this the sheet is navigation-only, and a phone-width visitor has
   * no reachable way to sign in or register anywhere on the public site
   * (#11039). Scoped to `md:hidden` so desktop never shows auth twice.
   */
  includeAuth?: boolean;
}

/**
 * The marketing-page hamburger: a ghost icon trigger + right sheet listing the
 * shared footer/legal links (via SidebarMenuContent → useFooterNavLinks), and
 * — below `sm`, when `includeAuth` is set — the auth buttons the header row
 * cannot fit at that width.
 *
 * Replaces the three hand-rolled copies that had drifted apart
 * (header-client + FloatingHeaderControls desktop/mobile).
 *
 * Owns its open state and closes itself on route change.
 */
export function HamburgerMenu({
  showSwitchers = true,
  includeAdvocateLanding = false,
  includeAuth = false,
}: HamburgerMenuProps) {
  const tNav = useTranslations('Navigation');
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className='h-9 w-9 min-h-11 min-w-11'
          aria-label={tNav('menu')}
        >
          <Menu className='h-5 w-5' />
        </Button>
      </SheetTrigger>
      <SheetContent side='right' className='w-[280px] overflow-y-auto'>
        {includeAuth && (
          <div className='mt-6 md:hidden border-b border-line-subtle pb-4'>
            <HeaderAuthSection
              layout='stacked'
              onItemClick={() => setIsOpen(false)}
            />
          </div>
        )}
        <nav className='mt-6'>
          <SidebarMenuContent
            showSwitchers={showSwitchers}
            showScheduleCall={false}
            showCookiePreferences
            includeAppLink
            includeAdvocateLanding={includeAdvocateLanding}
            itemHeight='h-10'
            onItemClick={() => setIsOpen(false)}
          />
        </nav>
      </SheetContent>
    </Sheet>
  );
}
